import React from "react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import { RETIRED_ACTION_NOTICE, isRetiredAction, normalizeAction } from "./adPolicyModel";

const ACTION_TONE = {
  mfa_required: { bg: "var(--an-dangerBg)", fg: "var(--an-danger)", bd: "var(--an-dangerBd)", dot: "var(--an-danger)", label: "Require MFA" },
  allow:        { bg: "var(--an-successBg)", fg: "var(--an-success)", bd: "var(--an-successBd)", dot: "var(--an-success)", label: "Allow" },
  block:        { bg: "var(--an-dangerBg)", fg: "var(--an-dangerFg)", bd: "var(--an-dangerBd)", dot: "var(--an-dangerFg)", label: "Deny" },
};

export function ActionPill({ action }) {
  const retired = isRetiredAction(action);
  const t = ACTION_TONE[normalizeAction(action)] || ACTION_TONE.allow;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "2px 9px 2px 8px",
          borderRadius: 99,
          background: t.bg,
          color: t.fg,
          border: `1px solid ${t.bd}`,
          fontSize: 11.5,
          fontWeight: 500,
          fontFamily: AN_FONT,
          whiteSpace: "nowrap",
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: t.dot }} />
        {t.label}
      </span>
      {retired && <RetiredTag />}
    </span>
  );
}

// Marks a policy still carrying the retired Notify flow. It evaluates as Allow;
// the tag exists so nobody reads that Allow as a deliberate choice.
export function RetiredTag() {
  return (
    <span
      title={RETIRED_ACTION_NOTICE}
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "1.5px 6px",
        borderRadius: 4,
        background: "#FEF1E0",
        color: "#B05A0A",
        border: "1px solid #F6D9A8",
        fontSize: 10,
        fontWeight: 600,
        fontFamily: AN_MONO,
        letterSpacing: 0.3,
        whiteSpace: "nowrap",
      }}
    >
      ACTION RETIRED
    </span>
  );
}

// The domain's enforcement posture — not a per-policy setting.
export function ModeBadge({ mode }) {
  const map = {
    enforce: { bg: "var(--an-successBg)", fg: "var(--an-success)",       bd: "var(--an-successBd)",      label: "Enforce" },
    monitor: { bg: "#EAF1FE", fg: "#1E55C9",       bd: "var(--an-infoBd)",      label: "Monitor" },
    unknown: { bg: AN_COLORS.bg, fg: AN_COLORS.muted, bd: AN_COLORS.line, label: "—" },
  };
  const t = map[mode] || map.unknown;
  return (
    <span
      title={
        mode === "monitor"
          ? "Monitor — the domain logs what policy would have done, and never blocks or prompts."
          : mode === "enforce"
          ? "Enforce — policy decisions are applied to live authentications."
          : "Enforcement posture unknown for this domain."
      }
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        borderRadius: 5,
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.bd}`,
        fontSize: 11,
        fontWeight: 500,
        fontFamily: AN_FONT,
        letterSpacing: 0.1,
      }}
    >
      {t.label}
    </span>
  );
}

export function ScopeTag({ scope }) {
  const map = {
    user:     { label: "user",     fg: "#5F4BB0",      bg: "#EFEBFB",     bd: "#DAD2F4" },
    group:    { label: "group",    fg: "#1E55C9",      bg: "#EAF1FE",     bd: "var(--an-infoBd)" },
    ou:       { label: "ou",       fg: "#1B6E55",      bg: "#E1F2EB",     bd: "#BFE4D2" },
    // Every user in the domain — an explicit opt-in, never inferred.
    domain:   { label: "domain",   fg: "#B05A0A",      bg: "#FEF1E0",     bd: "#F6D9A8" },
    // No targets and no domain-wide opt-in: this policy matches nobody.
    none:     { label: "unscoped", fg: AN_COLORS.muted, bg: AN_COLORS.bg, bd: AN_COLORS.line },
  };
  const t = map[scope] || map.group;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "1.5px 7px",
        borderRadius: 4,
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.bd}`,
        fontSize: 10.5,
        fontWeight: 600,
        fontFamily: AN_MONO,
        letterSpacing: 0.5,
        textTransform: "lowercase",
      }}
    >
      {t.label}
    </span>
  );
}

export function AuthTypeChip({ type, on = true }) {
  const map = {
    ad:       { label: "AD",     full: "Active Directory" },
    entra_id: { label: "Entra",  full: "Entra ID (cloud-joined)" },
    radius:   { label: "RADIUS", full: "VPN / Wi-Fi (RADIUS)" },
  };
  const t = map[type];
  if (!t) return null;
  return (
    <span
      title={t.full}
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "1.5px 6px",
        borderRadius: 4,
        background: on ? AN_COLORS.primaryL : AN_COLORS.bg,
        color: on ? AN_COLORS.primary : AN_COLORS.muted,
        border: `1px solid ${on ? "var(--an-infoBd)" : AN_COLORS.line}`,
        fontSize: 10.5,
        fontWeight: 600,
        fontFamily: AN_MONO,
        letterSpacing: 0.4,
      }}
    >
      {t.label}
    </span>
  );
}

export function StatusPill({ status }) {
  const tone =
    status === "Approved"
      ? { bg: "var(--an-successBg)", fg: AN_COLORS.success, bd: "var(--an-successBd)" }
      : status === "Pending"
      ? { bg: "var(--an-warnBg)", fg: "var(--an-warn)", bd: "var(--an-warnBd)" }
      : status === "Suspended"
      ? { bg: AN_COLORS.bg, fg: AN_COLORS.muted, bd: AN_COLORS.line }
      : { bg: AN_COLORS.bg, fg: AN_COLORS.muted, bd: AN_COLORS.line };
  const label =
    status === "Approved"
      ? "Active"
      : status === "Suspended"
      ? "Paused"
      : status || "—";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 9px",
        borderRadius: 99,
        background: tone.bg,
        color: tone.fg,
        border: `1px solid ${tone.bd}`,
        fontSize: 11.5,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

export function TargetCell({ targets, scope }) {
  if (!targets || targets.length === 0) {
    return <span style={{ color: AN_COLORS.muted, fontSize: 12 }}>—</span>;
  }
  const first = targets[0];
  const more = targets.length - 1;
  const iconColor = scope === "user" ? "#5F4BB0" : scope === "ou" ? "#1B6E55" : "#1E55C9";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, maxWidth: "100%" }}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "3px 9px 3px 7px",
          background: "var(--an-surface)",
          border: `1px solid ${AN_COLORS.line}`,
          borderRadius: 99,
          color: AN_COLORS.ink2,
          fontSize: 12,
          fontFamily: AN_FONT,
          maxWidth: 220,
          overflow: "hidden",
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: iconColor, flexShrink: 0 }} />
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontFamily: scope === "ou" ? AN_MONO : AN_FONT,
            fontSize: scope === "ou" ? 11.5 : 12,
          }}
        >
          {first}
        </span>
      </span>
      {more > 0 && (
        <span style={{ fontSize: 11, color: AN_COLORS.muted, fontFamily: AN_MONO }}>
          +{more}
        </span>
      )}
    </span>
  );
}

export function ClassBadge({ tone }) {
  const map = {
    privileged: { bg: "var(--an-dangerBg)", fg: "var(--an-danger)", bd: "var(--an-dangerBd)", label: "Privileged" },
    standard:   { bg: "#EAF1FE", fg: "#1E55C9", bd: "var(--an-infoBd)", label: "Standard" },
    service:    { bg: AN_COLORS.bg, fg: AN_COLORS.muted, bd: AN_COLORS.line, label: "Service" },
  };
  const t = map[tone] || map.standard;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "2px 9px 2px 7px",
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.bd}`,
        borderRadius: 99,
        fontSize: 11,
        fontWeight: 600,
        fontFamily: AN_FONT,
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: t.fg }} />
      {t.label}
    </span>
  );
}

// Derive scope from policyJson (group / user / ou / domain / none).
// An empty selection is NOT domain-wide — matchAll has to say so.
export function deriveScope(policyJson) {
  const ad = policyJson?.ad || {};
  if (ad.groups && ad.groups.length > 0) return "group";
  if (ad.users && ad.users.length > 0) return "user";
  if (ad.ous && ad.ous.length > 0) return "ou";
  if (ad.matchAll) return "domain";
  return "none";
}

export function deriveTargets(policyJson) {
  const ad = policyJson?.ad || {};
  if (ad.groups && ad.groups.length > 0) return ad.groups;
  if (ad.users && ad.users.length > 0) return ad.users;
  if (ad.ous && ad.ous.length > 0) return ad.ous;
  if (ad.matchAll) return [`Every user in ${ad.domain || "this domain"}`];
  return [];
}
